import React, { useEffect, useMemo, useState } from 'react';
import Animated, { runOnJS, useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';

import { Text, TextProps } from '@/components/TextComponent';
import { Box, BoxProps } from '@/components/lib/components';
import { textVariants } from '@/components/lib/typography';
import { usePrevious } from '@/hooks/use-previous';

type AnimatedNumberProps = {
    value: number;
    decimals?: number;
    appendText?: string;
    alignText?: BoxProps['alignItems'];
    displayFormat?: (value: number) => string | number;
    animate?: boolean;
} & TextProps;

export const AnimatedNumber = React.memo(
    ({
        value,
        appendText,
        decimals = 2,
        alignText = 'flex-end',
        displayFormat = (val: number) => val.toFixed(decimals),
        animate = true,
        ...textProps
    }: AnimatedNumberProps) => {
        const prevValue = usePrevious(value);
        const [displayValue, setDisplayValue] = useState(value);

        const lineHeight =
            (textProps.lineHeight as number) ??
            textVariants[(textProps.variant as keyof typeof textVariants) || 'bodyMedium'].lineHeight;

        const translateY = useSharedValue(0);

        const textAlignValue = alignText === 'flex-end' ? 'right' : 'left';

        useEffect(() => {
            if (!animate || prevValue === undefined || prevValue === value) {
                return;
            }

            const direction = Math.sign(value - prevValue);
            const offset = direction * lineHeight;

            // Slide out, update, slide in
            translateY.value = withTiming(offset, { duration: 150 }, () => {
                runOnJS(setDisplayValue)(value);
                translateY.value = -offset;
                translateY.value = withTiming(0, { duration: 150 });
            });
        }, [value, prevValue, animate, lineHeight, translateY]);

        const animatedStyle = useAnimatedStyle(() => ({
            transform: [{ translateY: translateY.value }],
        }));

        const formattedDisplayValue = useMemo(() => displayFormat(displayValue), [displayValue, displayFormat]);

        return (
            <Box flexDirection="row-reverse">
                {appendText ? (
                    <Box>
                        <Text {...textProps}>{appendText}</Text>
                    </Box>
                ) : null}
                <Box alignItems={alignText}>
                    <Box overflow={'hidden'} height={lineHeight}>
                        <Animated.View style={animatedStyle}>
                            <Text {...textProps} textAlign={textAlignValue}>
                                {formattedDisplayValue}
                            </Text>
                        </Animated.View>
                    </Box>
                </Box>
            </Box>
        );
    }
);
